<template>
  <div>
    <div class="actions row col-lg-12 mb-3 p-0 m-0 justify-content-start">
      <div class="actions-left">
        <div class="btn-group" role="group">
          <select class="form-control" v-model="experienceFilter" @change="onExperienceFilterChanged">
            <option value="">{{ $t('statistics.allExperiences') }}</option>
            <option v-for="experience in experiences" :key="experience.id" :value="experience.id">
              {{ experience.name }}
            </option>
          </select>
        </div>
      </div>
    </div>
    <CTableWrapper 
      ref="table" 
      hover 
      striped 
      :caption="$t('satisfactionFormAnswers')" 
      :items="items" 
      :actions="actions" 
      :pages="pages" 
      :fields="fields" 
      clickable-rows 
      @row-clicked="rowClicked" 
      @action-clicked="actionClicked" 
      @update-requested="updateItemsRequested" 
      :sorter="{external: true}" 
      :sorterValue="sorterValue" 
      @sorter-value-changed="sorterValueChanged" 
      :loading="loading">
    </CTableWrapper>
    
    <!-- Modal para ver detalhes da resposta -->
    <CModal
      :show.sync="showDetailModal"
      :title="$t('viewDetails')"
      color="primary"
      size="lg"
    >
      <div v-if="selectedAnswer">
        <div class="mb-3">
          <strong>{{ $t('reservations.tourist') }}:</strong> {{ selectedAnswer.touristName }}
        </div>
        <div class="mb-3">
          <strong>{{ $t('reservations.experience') }}:</strong> {{ selectedAnswer.experienceName }}
        </div>
        <div class="mb-3">
          <strong>{{ $t('reservations.date') }}:</strong> {{ formatDate(selectedAnswer.reservationDate) }}
        </div>
        <div class="mb-3">
          <strong>{{ $t('reservations.email') }}:</strong> {{ selectedAnswer.touristEmail }}
        </div>
        <div class="mb-3">
          <strong>{{ $t('reservations.id') }}:</strong> {{ selectedAnswer.reservationId }}
        </div>
        <hr>
        <h5 class="mb-3">{{ $t('answers') }}</h5>
        <div v-for="(qa, index) in selectedAnswer.answers" :key="qa.questionId" class="mb-4">
          <div class="question-header mb-2">
            <strong>{{ index + 1 }}. {{ qa.questionText }}</strong>
          </div>
          <div class="answer-content p-3 bg-light rounded">
            {{ qa.answer }}
          </div>
        </div>
      </div>
      
      <template #footer>
        <CButton @click="showDetailModal = false" color="secondary">{{ $t('close') }}</CButton>
      </template>
    </CModal>
  </div>
</template>
 
<script>
import { mapState, mapActions } from 'vuex'
import CTableWrapper from "@/controls/PaginatedTable.vue";
import InputGetList from "@/classes/InputGetList";
import i18n from '@/helpers/i18n';

export default {
  components: { CTableWrapper },
  data () {
    return {
      itemsPerPage: localStorage.itemsPerPage ?? 10,
      experienceFilter: '',
      currentPage: 1,
      fields: [
        { key: 'touristName', label: i18n.t('reservations.tourist'), _classes: 'font-weight-bold', sortable: true },
        { key: 'experienceName', label: i18n.t('reservations.experience'), sortable: true },
        { key: 'reservationDate', label: i18n.t('reservations.date'), sortable: true },
        { key: 'answersCount', label: i18n.t('answersCount'), sortable: false },
      ],
      actions: [
        { key: 'view', label: i18n.t('view') }
      ],
      sorterValue: { column: 'reservationDate', asc: false },
      loading: false,
      showDetailModal: false,
      selectedAnswer: null
    }
  },
  computed: {
    ...mapState('satisfactionFormAnswers', ['getSatisfactionFormAnswersOutput']),
    ...mapState('experiences', ['getExperiencesOutput']),
    ...mapState({ 
      alertType: state => state.alert.type,
      alertOutput: state => state.alert.output
    }),
    experiences: function() {
      return (this.getExperiencesOutput && this.getExperiencesOutput.data) ? this.getExperiencesOutput.data : [];
    },
    answersInfo: function() {
      return this.getSatisfactionFormAnswersOutput ? this.getSatisfactionFormAnswersOutput : null;
    },
    items: function()
    {
      if (!this.answersInfo || !this.answersInfo.data) return [];
      
      return this.answersInfo.data.map(item => ({
        ...item,
        reservationDate: this.formatDate(item.reservationDate),
        answersCount: item.answers ? item.answers.length : 0
      }));
    },
    pages: function()
    {
      return this.answersInfo ? this.answersInfo.totalPages : 1;
    }
  },
  mounted: async function()
  {
    // Load experiences first for the filter dropdown
    try {
      await this.getExperiences({ 
        pageSize: 100, 
        pageNumber: 1,
        propertyName: 'Position',
        orderBy: 1
      });
    } catch (error) {
      console.error('Error loading experiences on mount:', error);
    }
    
    this.updateItemsRequested(1, this.itemsPerPage);
  },
  methods: {
    ...mapActions('satisfactionFormAnswers', ['getSatisfactionFormAnswers']),
    ...mapActions('experiences', ['getExperiences']),
    formatDate(dateStr) {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      return date.toLocaleDateString('pt-PT');
    },
    formatDateTime(dateStr) {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      return date.toLocaleString('pt-PT');
    },
    rowClicked (item, index) {
      this.openDetailModal(item);
    },
    actionClicked(item, action){
      switch(action.key)
      {
        case 'view':
        {
          this.openDetailModal(item);
          break;
        }
        default:
        {
          throw "The provided action is not supported!";
        }
      }
    },
    openDetailModal(item) {
      // Find original item from answersInfo to preserve all data
      const originalItem = this.answersInfo && this.answersInfo.data 
        ? this.answersInfo.data.find(a => a.id === item.id) 
        : item;
      
      this.selectedAnswer = originalItem;
      this.showDetailModal = true;
    },
    updateItemsRequested(currentPage, itemsPerPage)
    {
      this.loading = true;
      this.itemsPerPage = itemsPerPage;
      this.currentPage = currentPage;

      var queryParams = {
        pageNumber: currentPage,
        pageSize: itemsPerPage
      };

      // Add experience filter if selected
      if (this.experienceFilter) {
        queryParams.experienceId = parseInt(this.experienceFilter);
      }

      this.getSatisfactionFormAnswers(queryParams);
    },
    onExperienceFilterChanged() {
      // Reset to page 1 when filter changes
      this.currentPage = 1;
      if (this.$refs.table) {
        this.$refs.table.reset();
      }
      this.updateItemsRequested(1, this.itemsPerPage);
    },
    sorterValueChanged(value)
    {
      this.sorterValue = value;
    }
  },
  watch:
  {
    sorterValue:
    {
      handler: function(val)
      {
        // Reset to page 1 when sorter changes
        this.currentPage = 1;
        if (this.$refs.table) {
          this.$refs.table.reset();
        }
        this.updateItemsRequested(1, this.itemsPerPage);
      },
      deep: true
    },
    getSatisfactionFormAnswersOutput: function(val)
    {
      if(val != null)
      {
        this.loading = false;
        // Sync currentPage in table component after data loads
        this.$nextTick(() => {
          if (this.$refs.table && this.currentPage) {
            this.$refs.table.currentPage = this.currentPage;
          }
        });
      }
    }
  }
}
</script>
 
<style>
th
{
  border-top: none !important;
}

tr
{
  cursor: pointer;
}

.question-header {
  font-size: 1.1em;
  color: #333;
}

.answer-content {
  color: #555;
  min-height: 40px;
}
</style>

